/**
 * 세션 캐시 — 같은 데이터를 다시 긁지 않기 위한 짧은 보관함.
 *
 * 정산 화면은 캠페인 1건당 조회 1번이라, 페이지를 나갔다 들어올 때마다 처음부터
 * 다시 긁으면 매번 몇 초를 기다린다. 탭 안에서만 유지되는 sessionStorage 에
 * 짧게(기본 5분) 담아 두면 재방문이 즉시 뜬다.
 *
 * 설계상 지키는 것:
 *  - **탭이 닫히면 사라진다** (sessionStorage) — 돈 숫자를 오래 들고 있지 않는다.
 *  - **저장 시각을 함께 보관** — 화면에 "N분 전 기준"을 표시해 오래된 값을 오해하지 않게.
 *  - **용량 초과·차단 환경에서 조용히 실패** — 캐시는 있으면 좋은 것이고 없어도 동작해야 한다.
 */

const PREFIX = 'blbent:cache:v1:';

/** 캐시에서 읽는다. 없거나 만료됐거나 깨져 있으면 null */
export const readCache = (key, ttlMs) => {
  try {
    const raw = sessionStorage.getItem(PREFIX + key);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.savedAt !== 'number') return null;

    const age = Date.now() - parsed.savedAt;
    if (age < 0 || age > ttlMs) {
      sessionStorage.removeItem(PREFIX + key);
      return null;
    }

    return { data: parsed.data, savedAt: parsed.savedAt, ageMs: age };
  } catch (error) {
    // 파싱 실패·접근 차단 — 캐시가 없는 것으로 취급한다
    return null;
  }
};

/** 캐시에 담는다. 실패해도 화면 동작에는 영향이 없어야 한다 */
export const writeCache = (key, data) => {
  try {
    sessionStorage.setItem(PREFIX + key, JSON.stringify({ savedAt: Date.now(), data }));
    return true;
  } catch (error) {
    // 용량 초과(QuotaExceededError)나 차단 환경 — 캐시 없이 그냥 간다
    return false;
  }
};

/** 특정 키를 지운다 (강제 새로고침용) */
export const clearCache = (key) => {
  try {
    sessionStorage.removeItem(PREFIX + key);
  } catch (error) {
    /* 무시 — 지울 수 없으면 TTL 로 어차피 만료된다 */
  }
};

/** 접두어가 같은 캐시를 모두 지운다 */
export const clearCachePrefix = (keyPrefix) => {
  try {
    const full = PREFIX + keyPrefix;
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith(full))
      .forEach((key) => sessionStorage.removeItem(key));
  } catch (error) {
    /* 무시 */
  }
};

/** "3분 전" 같은 사람이 읽는 경과 시간 */
export const describeAge = (ageMs) => {
  if (typeof ageMs !== 'number' || ageMs < 0) return '';
  const minutes = Math.floor(ageMs / 60000);
  if (minutes < 1) return '방금';
  if (minutes < 60) return `${minutes}분 전`;
  const hours = Math.floor(minutes / 60);
  return `${hours}시간 전`;
};

const sessionCache = { readCache, writeCache, clearCache, clearCachePrefix, describeAge };

export default sessionCache;
